/**
 * i18n bootstrap for RivoNes (Spanish default, English alternative).
 *
 * Detection order:
 *  1. `?lng=` query param  → override (useful for QA + share links)
 *  2. localStorage["rivones_lang"]
 *  3. navigator language (es-* → "es", en-* → "en", anything else → fallback)
 *  4. fallback "es"
 *
 * Once a logged-in user updates their language (via the profile dropdown or
 * the LanguageSwitcher in Navbar), `LanguageProvider.setLanguage()` PATCHes
 * `PUT /api/auth/language` so it persists across devices and is used by all
 * server-side emails/SMS/notifications routed to that user.
 *
 * To add a new locale (e.g. fr.json), add the import here and append the code
 * to `SUPPORTED_LANGUAGES`. No code changes elsewhere are required because
 * every translated string flows through the `t()` function.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import es from "./locales/es.json";
import en from "./locales/en.json";

export const SUPPORTED_LANGUAGES = [
  { code: "es", label: "Español", flag: "🇲🇽" },
  { code: "en", label: "English", flag: "🇺🇸" },
];

export const DEFAULT_LANGUAGE = "es";
export const LANG_STORAGE_KEY = "rivones_lang";

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      es: { translation: es },
      en: { translation: en },
    },
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
    // Match navigator codes "en-US", "es-MX" → "en"/"es"
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      // RivoNes is Spanish-first (Mexico). Navigator language was producing
      // unwanted English UI for guests with browser=en-* (e.g. travelers from
      // the US). Spanish is now the canonical default; users opt into English
      // via the Navbar LanguageSwitcher (writes to localStorage).
      order: ["querystring", "localStorage"],
      lookupQuerystring: "lng",
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    returnNull: false,
  });

// Keep <html lang> in sync for screen readers / SEO.
const syncHtmlLang = (lng) => {
  try {
    document.documentElement.lang = (lng || "es").startsWith("en") ? "en" : "es-MX";
  } catch (_e) {
    /* SSR / tests */
  }
};
syncHtmlLang(i18n.resolvedLanguage || i18n.language);
i18n.on("languageChanged", syncHtmlLang);

export default i18n;
